import { gql } from '@apollo/client'
import { buildGraphqlQuery } from '@ztechapps/common.utils'

// `currency` is intentionally omitted by callers that let the server resolve it — GrantCredits
// falls back to the target owner's existing account (or the app's default currency for a
// first-ever grant). Callers that already know the account currency (e.g. a self-service top-up
// that already fetched its own balance) may pass it explicitly.
export const GRANT_CREDITS_MUTATION = gql`
  mutation grantCredits($input: GrantCreditsArgs!) {
    grantCredits(input: $input) {
      _id
      ownerId
      currency
      credits
      balanceAfter
    }
  }
`

export const MY_CREDIT_BALANCE_QUERY = gql`
  query myCreditBalance {
    myCreditBalance {
      _id
      ownerId
      balance
      currency
    }
  }
`

// The tenant's AI credit debits for the usage table: paginated and filter/sort-aware, paired with a
// sibling `aiCreditTransactionListMeta` for `pageInfo` (useList's useBuildListQueryResults reads
// `${entity}Meta` as a sibling field). The resolver requires `filter.ownerId` and `canManageAiBilling`
// and returns Debit rows only; `filter.createdBy` narrows to one user. `createdByName` and
// `createdByEmail` are blank on a row with no recorded user. An app with its own row selection
// passes its own list query to the table instead.
export const AI_CREDIT_TRANSACTION_LIST_QUERY = gql`
  query aiCreditTransactionList($filter: JSON, $options: JSON) {
    aiCreditTransactionList(filter: $filter, options: $options) {
      _id
      feature
      remarks
      model
      inputTokens
      outputTokens
      rawUsd
      credits
      balanceAfter
      currency
      createdAt
      markupCcy
      markupPct
      createdBy
      createdByName
      createdByEmail
    }

    aiCreditTransactionListMeta(filter: $filter) {
      count
    }
  }
`

// Aggregate totals for the SAME filter driving `aiCreditTransactionList`, aggregated server-side so
// the bottom-totals line stays accurate under pagination.
export const AI_CREDIT_TRANSACTION_LIST_GET_TOTALS_QUERY = gql`
  query aiCreditTransactionListGetTotals($filter: JSON) {
    aiCreditTransactionListGetTotals(filter: $filter) {
      totalTokens
      totalCredits
      totalMarkup
    }
  }
`

export const ALL_CREDIT_PACKAGE_QUERY = gql`
  query allCreditPackage($filter: JSON!, $options: JSON) {
    allCreditPackage(filter: $filter, options: $options) {
      _id
      name
      credits
      price
      currency
    }
  }
`

// `validateCoupon` is a GraphQL Query, not a Mutation, so BuyCreditsModal runs it on the "Apply"
// click through `useLazyDocument` (Apollo's `useMutation` throws when handed a Query document),
// with `network-only` so every Apply re-checks the coupon against the server rather than
// returning a stale cached answer.
export const VALIDATE_COUPON_QUERY = gql`
  query validateCoupon($input: ValidateCouponArgs!) {
    validateCoupon(input: $input) {
      discountAmount
      finalAmount
      coupon {
        _id
        code
      }
    }
  }
`

// The symbol/decimals a package's price is displayed with — read from the package currency's own
// `CurrencyRate` row. `currency` is uniquely indexed on CurrencyRate, so it is always a
// fetch-one-row-by-filter (`singleCurrencyRate`).
const currencyRateDisplayFragment = gql`
  fragment currencyRateDisplayFragment on CurrencyRate {
    _id
    currency
    symbol
    decimals
  }
`
export const currencyRateQueries = buildGraphqlQuery('CurrencyRate', currencyRateDisplayFragment)

export const CREATE_CREDIT_PURCHASE_MUTATION = gql`
  mutation createCreditPurchase($input: CreateCreditPurchaseArgs!) {
    createCreditPurchase(input: $input) {
      purchaseId
      paid
      checkoutUrl
    }
  }
`
